import React from 'react'
import type {
  LotStoryArtifactDetailV1,
  LotStoryArtifactVersionV1
} from '../../types/lotStory'
import type { TranslateFn } from './lotStoryLibraryTypes'
import type { LotStoryLibraryPresentation } from './lotStoryPresentation'
import {
  getLotStoryArtifactVersionBadgeStyle,
  getLotStoryArtifactVersionLabel,
  getLotStoryReadinessBadgeStyle,
  getLotStoryReadinessLabel
} from './lotStoryLibraryUi'

type LotStoryLibraryCompareSnapshotCardProps = {
  t: TranslateFn
  artifact: LotStoryArtifactDetailV1
  heading: string
  version: LotStoryArtifactVersionV1
  selectedStoryPreviewVersionId: string | null
  accentBorder: string
  accentBackground: string
  accentText: string
  onOpenArtifactVersion: (artifact: LotStoryArtifactDetailV1, version: LotStoryArtifactVersionV1) => void
  presentation: LotStoryLibraryPresentation
}

export default function LotStoryLibraryCompareSnapshotCard({
  t,
  artifact,
  heading,
  version,
  selectedStoryPreviewVersionId,
  accentBorder,
  accentBackground,
  accentText,
  onOpenArtifactVersion,
  presentation
}: Readonly<LotStoryLibraryCompareSnapshotCardProps>) {
  const {
    formatHistoryTimestamp,
    renderStoryProjectionReadOnlySections
  } = presentation
  const projection = version.projectionSnapshot
  const isSnapshotOpen = selectedStoryPreviewVersionId === version.artifactVersionId

  return (
    <div
      style={{
        border: `1px solid ${accentBorder}`,
        borderRadius: 10,
        backgroundColor: accentBackground,
        padding: 12,
        display: 'grid',
        gap: 10
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: 4 }}>
          <strong style={{ fontSize: 13, color: accentText }}>{heading}</strong>
          <div style={{ fontSize: 13, color: '#111827' }}>{version.title}</div>
        </div>
        <button
          type="button"
          onClick={() => onOpenArtifactVersion(artifact, version)}
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            border: isSnapshotOpen ? '1px solid #60a5fa' : '1px solid #cbd5e1',
            backgroundColor: isSnapshotOpen ? '#eff6ff' : '#ffffff',
            color: isSnapshotOpen ? '#1d4ed8' : '#0f172a',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600
          }}
        >
          {isSnapshotOpen ? t('lots.story.library.history.snapshotOpen') : t('lots.story.library.history.openSnapshot')}
        </button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <span style={{ ...getLotStoryReadinessBadgeStyle(projection.readiness.status), padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {getLotStoryReadinessLabel(t, projection.readiness.status)}
        </span>
        <span style={{ ...getLotStoryArtifactVersionBadgeStyle(version.versionStatus), padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {getLotStoryArtifactVersionLabel(t, version.versionStatus)}
        </span>
        <span style={{ fontSize: 12, color: accentText }}>
          {t('lots.story.summary.generatedAt')}: {formatHistoryTimestamp(version.projectionGeneratedAt)}
        </span>
      </div>

      {renderStoryProjectionReadOnlySections({
        projection,
        keyPrefix: version.artifactVersionId,
        accentText,
        accentBorder,
        compact: true,
        footerContent: (
          <>
            {version.summary ? (
              <div style={{ display: 'grid', gap: 4 }}>
                <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.draft.field.summary')}</strong>
                <div style={{ fontSize: 13, color: '#111827' }}>{version.summary}</div>
              </div>
            ) : null}

            {version.notes ? (
              <div style={{ display: 'grid', gap: 4 }}>
                <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.draft.field.notes')}</strong>
                <div style={{ fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap' }}>{version.notes}</div>
              </div>
            ) : null}
          </>
        )
      })}
    </div>
  )
}